import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Header from './components/Header';
import Landing from './components/Landing';
import Transcriber from './components/Transcriber';
import Login from './components/Login';
import Register from './components/Register';
import Profile from './components/Profile';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState(null); 
  const [transcriptionCount, setTranscriptionCount] = useState(() => {
    const saved = localStorage.getItem('transcription_count');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [isRegistered, setIsRegistered] = useState(() => {
    return localStorage.getItem('is_registered') === 'true' || !!localStorage.getItem('token');
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/users/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setUser(data);
          setIsRegistered(true);
        } else {
          localStorage.removeItem('token');
        }
      })
      .catch(() => localStorage.removeItem('token'));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('transcription_count', transcriptionCount);
    localStorage.setItem('is_registered', isRegistered);
  }, [transcriptionCount, isRegistered]);

  const handleGetStarted = () => {
    if (!isRegistered && transcriptionCount >= 3) {
      // 3 essais terminés → bloquer et montrer l'inscription
      setAuthMode('register');
    } else {
      setCurrentPage('transcriber');
    }
  };

  const handleLoginSuccess = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsRegistered(true);
    setAuthMode(null);
    setCurrentPage('transcriber');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsRegistered(false);
    setCurrentPage('landing');
  };

  const handleUserUpdate = (updatedData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedData
    }));
  };

  return (
    <div className="App min-h-screen bg-slate-900">
      <Header 
        onGetStarted={handleGetStarted} 
        user={user} 
        onLogout={handleLogout}
        onLoginClick={() => setAuthMode('login')}
        onProfileClick={() => setCurrentPage('profile')}
      />

      {currentPage === 'landing' && <Landing onGetStarted={handleGetStarted} />}
      
      {currentPage === 'transcriber' && (
        <Transcriber 
          onBackHome={() => setCurrentPage('landing')} 
          usageCount={transcriptionCount}
          setUsageCount={setTranscriptionCount}
          isRegistered={isRegistered}
          onRegisterSuccess={(userData) => {
            setIsRegistered(true);
            setUser(userData);
            setAuthMode(null);
            setCurrentPage('transcriber');
          }}
        />
      )}

      {currentPage === 'profile' && (
        <Profile 
          user={user} 
          onBack={() => setCurrentPage('transcriber')} 
          onLogout={handleLogout}
          onUserUpdate={handleUserUpdate}
        />
      )}

      {authMode === 'login' && (
        <Login 
          onClose={() => setAuthMode(null)} 
          onLoginSuccess={handleLoginSuccess}
          onSwitchToRegister={() => setAuthMode('register')}
        />
      )}

      {authMode === 'register' && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setAuthMode(null)} 
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10 p-1 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <Register 
              onRegisterSuccess={(userData) => {
                setIsRegistered(true);
                setUser(userData);
                setAuthMode(null);
                setCurrentPage('transcriber');
              }} 
              onSwitchToLogin={() => setAuthMode('login')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
